import {useCallback, useMemo} from "react";
import {v4 as UUID} from "uuid";
import type {Door, GridPoint, LevelGeometryDocument, LevelTransition} from "@/dungeons/model/DungeonGeometry.ts";
import {validTransitionPolygon} from "@/dungeons/geometry/TransitionPolygon.ts";
import type {GeometryDocumentUpdate} from "@/dungeons/editor/shared/useLevelGeometryDocument.ts";
import {
    addDoor,
    addTransition,
    addWall,
    deleteFeature,
    featureExists,
    moveDoorPoint,
    moveTransitionPoint,
    moveWallPoint,
    pointsEqual,
    removeWallPoint,
    removeTransitionPoint,
    updateDoor,
    updateTransition,
} from "./GeometryDocumentOperations.ts";
import {useDungeonEditorState, type DungeonEditorState} from "./DungeonEditorState.ts";
import type {DoorEndpoint, GeometryEditorTool, SelectedGeometryFeature} from "./GeometryEditorTypes.ts";

type DoorChanges = Partial<Pick<Door, "initialState" | "secret" | "label" | "notes">>;

export type GeometryEditorCommands = {
    selectTool: (tool: GeometryEditorTool) => void;
    addWallPoint: (point: GridPoint) => void;
    finishWall: () => void;
    wallContextMenu: () => void;
    clearWallDraft: () => void;
    addTransitionPoint: (point: GridPoint) => void;
    finishTransition: () => void;
    transitionContextMenu: () => void;
    clearTransitionDraft: () => void;
    placeDoorPoint: (point: GridPoint) => void;
    doorContextMenu: () => void;
    clearDoorDraft: () => void;
    select: (selection?: SelectedGeometryFeature) => void;
    moveWallPoint: (wallId: string, pointIndex: number, point: GridPoint) => void;
    removeWallPoint: (wallId: string, pointIndex: number) => void;
    moveDoorPoint: (doorId: string, endpoint: DoorEndpoint, point: GridPoint) => void;
    updateDoor: (doorId: string, changes: DoorChanges) => void;
    moveTransitionPoint: (id: string, index: number, point: GridPoint) => void;
    removeTransitionPoint: (id: string, index: number) => void;
    updateTransition: (id: string, changes: Partial<Pick<LevelTransition,
        "kind" | "label" | "targetLevelId" | "arrivalImagePosition">>) => void;
    deleteSelection: () => void;
};

export type GeometryEditorController = {
    document: LevelGeometryDocument;
    state: DungeonEditorState;
    selectedWall?: LevelGeometryDocument["walls"][number];
    selectedDoor?: Door;
    selectedTransition?: LevelTransition;
    commands: GeometryEditorCommands;
};

export default function useGeometryEditorController({document, updateDocument}: {
    document: LevelGeometryDocument;
    updateDocument: (update: GeometryDocumentUpdate) => void;
}): GeometryEditorController {
    const {state: rawState, dispatch} = useDungeonEditorState();
    const selection = rawState.selection && featureExists(document, rawState.selection)
        ? rawState.selection
        : undefined;
    const state = useMemo(() => selection === rawState.selection
        ? rawState
        : {...rawState, selection}, [rawState, selection]);
    const selectedWall = selection?.type === "wall"
        ? document.walls.find(wall => wall.id === selection.id)
        : undefined;
    const selectedDoor = selection?.type === "door"
        ? document.doors.find(door => door.id === selection.id)
        : undefined;
    const selectedTransition = selection?.type === "transition"
        ? document.transitions.find(item => item.id === selection.id)
        : undefined;

    const selectTool = useCallback((tool: GeometryEditorTool) => dispatch({type: "SET_TOOL", tool}), [dispatch]);
    const addWallPoint = useCallback((point: GridPoint) =>
        dispatch({type: "ADD_WALL_POINT", point}), [dispatch]);
    const finishWall = useCallback(() => {
        if (state.wallDraft.length < 2) return;
        const wallId = UUID();
        updateDocument(current => addWall(current, {id: wallId, points: state.wallDraft}));
        dispatch({type: "FINISH_WALL", wallId});
    }, [dispatch, state.wallDraft, updateDocument]);
    const addTransitionPoint = useCallback((point: GridPoint) =>
        dispatch({type: "ADD_TRANSITION_POINT", point}), [dispatch]);
    const finishTransition = useCallback(() => {
        const points = state.transitionDraft;
        if (!validTransitionPolygon(points)) return;
        const transitionId = UUID();
        updateDocument(current => addTransition(current, {id: transitionId, points, kind: "OTHER"}));
        dispatch({type: "FINISH_TRANSITION", transitionId});
    }, [dispatch, state.transitionDraft, updateDocument]);
    const placeDoorPoint = useCallback((point: GridPoint) => {
        const start = state.doorDraft;
        if (!start) {
            dispatch({type: "START_DOOR", point});
            return;
        }
        if (pointsEqual(start, point)) return;
        const doorId = UUID();
        updateDocument(current => addDoor(current, {
            id: doorId,
            start,
            end: point,
            initialState: "CLOSED",
            secret: false,
        }));
        dispatch({type: "FINISH_DOOR", doorId});
    }, [dispatch, state.doorDraft, updateDocument]);
    const select = useCallback((next?: SelectedGeometryFeature) =>
        dispatch({type: "SELECT_FEATURE", selection: next}), [dispatch]);
    const deleteSelection = useCallback(() => {
        if (!selection) return;
        updateDocument(current => deleteFeature(current, selection));
        select();
    }, [select, selection, updateDocument]);

    const commands = useMemo<GeometryEditorCommands>(() => ({
        selectTool,
        addWallPoint,
        finishWall,
        wallContextMenu: () => dispatch({type: "WALL_CONTEXT_MENU"}),
        clearWallDraft: () => dispatch({type: "CLEAR_WALL_DRAFT"}),
        addTransitionPoint,
        finishTransition,
        transitionContextMenu: () => dispatch({type: "TRANSITION_CONTEXT_MENU"}),
        clearTransitionDraft: () => dispatch({type: "CLEAR_TRANSITION_DRAFT"}),
        placeDoorPoint,
        doorContextMenu: () => dispatch({type: "DOOR_CONTEXT_MENU"}),
        clearDoorDraft: () => dispatch({type: "CLEAR_DOOR_DRAFT"}),
        select,
        moveWallPoint: (wallId, pointIndex, point) => updateDocument(
            current => moveWallPoint(current, wallId, pointIndex, point),
        ),
        removeWallPoint: (wallId, pointIndex) => updateDocument(
            current => removeWallPoint(current, wallId, pointIndex),
        ),
        moveDoorPoint: (doorId, endpoint, point) => updateDocument(
            current => moveDoorPoint(current, doorId, endpoint, point),
        ),
        updateDoor: (doorId, changes) => updateDocument(current => updateDoor(current, doorId, changes)),
        moveTransitionPoint: (id, index, point) => updateDocument(
            current => moveTransitionPoint(current, id, index, point)),
        removeTransitionPoint: (id, index) => updateDocument(
            current => removeTransitionPoint(current, id, index)),
        updateTransition: (id, changes) => updateDocument(current => updateTransition(current, id, changes)),
        deleteSelection,
    }), [addTransitionPoint, addWallPoint, deleteSelection, dispatch, finishTransition, finishWall,
        placeDoorPoint, select, selectTool, updateDocument]);

    return {document, state, selectedWall, selectedDoor, selectedTransition, commands};
}
